import { lazy, Suspense } from "react";
import { createBrowserRouter } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import AppLayout from "@/components/Layout/AppLayout";

const DashboardPage = lazy(() => import("@/pages/Dashboard/DashboardPage"));
const AssetsListPage = lazy(() => import("@/pages/Assets/AssetsListPage"));
const AssetDetailPage = lazy(() => import("@/pages/Assets/AssetDetailPage"));
const AssetFormPage = lazy(() => import("@/pages/Assets/AssetFormPage"));
const AssetImportPage = lazy(() => import("@/pages/Assets/AssetImportPage"));
const GenerateCodesHubPage = lazy(() => import("@/pages/Assets/GenerateCodes/GenerateCodesHubPage"));
const ScanVendorCodesPage = lazy(() => import("@/pages/Assets/GenerateCodes/ScanVendorCodesPage"));
const GenerateNewAssetsPage = lazy(() => import("@/pages/Assets/GenerateCodes/GenerateNewAssetsPage"));
const GenerateExistingAssetsPage = lazy(() => import("@/pages/Assets/GenerateCodes/GenerateExistingAssetsPage"));
const PrintCustomLabelsPage = lazy(() => import("@/pages/Assets/GenerateCodes/PrintCustomLabelsPage"));
const AssetRequestsPage = lazy(() => import("@/pages/Assets/AssetRequestsPage"));
const AssignmentsPage = lazy(() => import("@/pages/Assignments/AssignmentsPage"));
const MaintenanceListPage = lazy(() => import("@/pages/Maintenance/MaintenanceListPage"));
const MaintenanceDetailPage = lazy(() => import("@/pages/Maintenance/MaintenanceDetailPage"));
const ScanPage = lazy(() => import("@/pages/Scan/ScanPage"));
const ReportsPage = lazy(() => import("@/pages/Reports/ReportsPage"));
const SettingsPage = lazy(() => import("@/pages/Settings/SettingsPage"));
const ProfilePage = lazy(() => import("@/pages/Profile/ProfilePage"));
const TeamPage = lazy(() => import("@/pages/Team/TeamPage"));
const UsersAdminPage = lazy(() => import("@/pages/Admin/UsersAdminPage"));
const UpgradePlansPage = lazy(() => import("@/pages/Billing/UpgradePlansPage"));
const CheckoutPage = lazy(() => import("@/pages/Billing/CheckoutPage"));

function PageFallback() {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
      <CircularProgress />
    </Box>
  );
}

function suspended(element: React.ReactNode) {
  return <Suspense fallback={<PageFallback />}>{element}</Suspense>;
}

/**
 * Route tree rendered inside AppLayout once the user is authenticated.
 * `mode`/`onToggleMode` are threaded through here (rather than context) to
 * keep the dark/light toggle trivially explicit for anyone extending it.
 *
 * Every page is code-split via React.lazy - AppLayout's <Outlet /> carries
 * its own Suspense boundary (see AppLayout.tsx) so route transitions inside
 * the app shell show a spinner instead of a blank frame, while the two
 * standalone routes below (outside AppLayout) each need their own boundary.
 */
export function createAppRouter(mode: "light" | "dark", onToggleMode: () => void) {
  return createBrowserRouter([
    {
      path: "/",
      element: <AppLayout mode={mode} onToggleMode={onToggleMode} />,
      children: [
        { index: true, element: <DashboardPage /> },
        { path: "assets", element: <AssetsListPage /> },
        { path: "assets/new", element: <AssetFormPage /> },
        { path: "assets/import", element: <AssetImportPage /> },
        { path: "assets/generate-codes", element: <GenerateCodesHubPage /> },
        { path: "assets/generate-codes/scan-vendor", element: <ScanVendorCodesPage /> },
        { path: "assets/generate-codes/new", element: <GenerateNewAssetsPage /> },
        { path: "assets/generate-codes/existing", element: <GenerateExistingAssetsPage /> },
        { path: "assets/generate-codes/print", element: <PrintCustomLabelsPage /> },
        { path: "asset-requests", element: <AssetRequestsPage /> },
        { path: "assets/:id/edit", element: <AssetFormPage /> },
        { path: "assets/:id", element: <AssetDetailPage /> },
        { path: "assignments", element: <AssignmentsPage /> },
        { path: "maintenance", element: <MaintenanceListPage /> },
        { path: "maintenance/:id", element: <MaintenanceDetailPage /> },
        { path: "scan", element: <ScanPage /> },
        { path: "reports", element: <ReportsPage /> },
        { path: "settings", element: <SettingsPage /> },
        { path: "profile", element: <ProfilePage /> },
        { path: "team", element: <TeamPage /> },
        { path: "admin/users", element: <UsersAdminPage /> }
      ]
    },
    // Standalone (no sidebar/header chrome), matching how a real "Store" upgrade
    // flow reads as a separate surface from the main app shell.
    { path: "billing/upgrade", element: suspended(<UpgradePlansPage />) },
    { path: "billing/checkout/:planName", element: suspended(<CheckoutPage />) }
  ]);
}
